import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useTheme } from "../contexts/ThemeContext";
import ErrorBoundary from "../components/ErrorBoundary";
import {
  Calendar, Users, FileText, FileSignature, CreditCard, MessageCircle,
  PartyPopper, ChefHat, Package, UserCog, DollarSign, Palette, Settings,
  Menu, X, LogOut, ChevronDown, ChevronRight, User, Home, Building2, Lock, BookOpen,
  Sparkles, Zap, FileWarning, TrendingUp, UtensilsCrossed, Layers, SlidersHorizontal
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import NotificacionesBell from "../components/NotificacionesBell";

/**
 * ════════════════════════════════════════════════════════════════════
 *  SIDEBAR DEFINITIVO · Iteración UX/UI de lanzamiento
 * ────────────────────────────────────────────────────────────────────
 *  Principios de diseño:
 *  1. Sidebar organizado en 6 secciones jerárquicas: Principal (superior
 *     sin encabezado) · Ventas · Operación · Catálogo · Comunicación ·
 *     Mis unidades (solo Celebra) · Administración (solo admin).
 *  2. Cada item responde a una intención clara del usuario.
 *  3. Nombres cortos, humanos, sin jerga técnica.
 *  4. Adaptación automática por ROL INTERNO (ventas, coordinacion, chef,
 *     mesero, cliente, admin) además del plan SaaS.
 *  5. Elementos secundarios accesibles desde el Expediente del Evento
 *     o desde el menú de usuario (Marca & Colores, Configuración).
 *
 *  Cada item soporta:
 *    · `mod`: clave del plan (oculto si el plan no lo incluye)
 *    · `roles`: roles internos autorizados. `*` = fallback para users
 *      sin rol_interno específico (users legacy o admin sin marca).
 *    · `celebraGroupOnly`: solo Celebra Group (interno)
 *    · `superAdminOnly`: solo Propietario Global
 * ════════════════════════════════════════════════════════════════════
 */
/**
 * ════════════════════════════════════════════════════════════════════
 *  SIDEBAR REORGANIZADO · Iter103 · Julio-2026
 *  Reorganizado según el flujo REAL del negocio (petición del usuario):
 *  Cotizador → Cliente → Cotización aprobada → Anticipo → Evento →
 *  Contrato → Planeación → Personal/Proveedores → Compras/Producción →
 *  Finanzas del evento → Ejecución → Cierre.
 *
 *  MENÚ PRINCIPAL (8 items exactos):
 *    1. Inicio
 *    2. Cotizador (marketplace)
 *    3. Clientes y cotizaciones
 *    4. Eventos
 *    5. Catálogo
 *    6. Agenda (una sola)
 *    7. Finanzas
 *    8. Administración (agrupador)
 *
 *  OCULTO del menú operativo (deep-link sigue funcionando):
 *    · Mi día · Mi Agenda + · Dashboard Celebra
 *    · Paneles por unidad (Petit / Kermesita / Bocatto)
 *
 *  SOLO SUPERADMIN DE PLATAFORMA (sección "Plataforma" separada):
 *    · Empresas SaaS · Planes · Auditoría · Sin clasificar · Páginas públicas
 * ════════════════════════════════════════════════════════════════════
 */
const NAV_PRINCIPAL = [
  { icon: Home,        label: "Inicio",     path: "/panel/mi-panel",  roles: ["*", "ventas", "coordinacion", "chef", "mesero", "cliente"], testid: "nav-inicio" },
  { icon: Zap,         label: "Cotizador",  path: "/panel/cotizar",   roles: ["*", "ventas"], mod: "cotizaciones", testid: "nav-cotizador" },
  {
    icon: Users, label: "Clientes y cotizaciones", path: "/panel/clientes", roles: ["*", "ventas"], mod: "clientes", testid: "nav-clientes",
    children: [
      { icon: Users,    label: "Clientes",     path: "/panel/clientes",   mod: "clientes",     testid: "nav-clientes-listado" },
      { icon: FileText, label: "Cotizaciones", path: "/panel/pipeline",   mod: "cotizaciones", testid: "nav-cotizaciones" },
      { icon: FileSignature, label: "Contratos", path: "/panel/contratos",mod: "contratos",   testid: "nav-contratos" },
      { icon: CreditCard,   label: "Pagos",     path: "/panel/pagos",     mod: "pagos",       testid: "nav-pagos" },
      { icon: MessageCircle, label: "CRM WhatsApp", path: "/panel/whatsapp", mod: "whatsapp", testid: "nav-crm-whatsapp" },
    ],
  },
  { icon: PartyPopper, label: "Eventos",    path: "/panel/eventos",   roles: ["*", "ventas", "coordinacion", "chef", "mesero"], mod: "eventos", testid: "nav-eventos" },
  {
    icon: ChefHat, label: "Operación", path: "/panel/produccion", roles: ["*", "coordinacion", "chef"], testid: "nav-operacion",
    children: [
      { icon: ChefHat,         label: "Producción",     path: "/panel/produccion", mod: "produccion", testid: "nav-produccion" },
      { icon: UtensilsCrossed, label: "Recetas",        path: "/panel/recetas",    mod: "recetas",    testid: "nav-recetas" },
      { icon: Package,         label: "Inventario y mermas", path: "/panel/inventario", mod: "inventario", testid: "nav-inventario" },
      { icon: FileText,        label: "Lista de compras",    path: "/panel/compras",    mod: "compras",    testid: "nav-compras" },
      { icon: Users,           label: "Personal",       path: "/panel/personal",   mod: "personal",   testid: "nav-personal" },
      { icon: Users,           label: "Proveedores",    path: "/panel/proveedores",mod: "proveedores",testid: "nav-proveedores" },
    ],
  },
  {
    icon: BookOpen, label: "Catálogo", path: "/panel/catalogo", roles: ["*"], mod: "catalogo", testid: "nav-catalogo",
    children: [
      { icon: Layers,             label: "Todo el catálogo",   path: "/panel/catalogo",          mod: "catalogo",     testid: "nav-catalogo-general" },
      { icon: Sparkles,           label: "Experiencias",        path: "/panel/experiencias",      mod: "catalogo",     testid: "nav-experiencias" },
      { icon: UtensilsCrossed,    label: "Menús",               path: "/panel/menus",             mod: "menus",        testid: "nav-menus" },
      { icon: Package,            label: "Servicios y extras",  path: "/panel/servicios-rapidos", mod: "catalogo",     testid: "nav-servicios-rapidos" },
      { icon: SlidersHorizontal,  label: "Config. cotizador",   path: "/panel/cotizador-config",  mod: "cotizador_publico", testid: "nav-cotizador-config" },
    ],
  },
  { icon: Calendar,    label: "Agenda",     path: "/panel/mi-agenda-personal", roles: ["*", "ventas", "coordinacion", "chef", "mesero", "cliente"], testid: "nav-agenda" },
  { icon: DollarSign,  label: "Finanzas",   path: "/panel/finanzas-pro", roles: ["*"], mod: "finanzas", testid: "nav-finanzas" },
];

// Fallback para planes que NO incluyen "eventos" (ej. plan_basico legacy).
const NAV_PRINCIPAL_FALLBACK_BASIC = [];

// Administración del propio tenant (operación diaria). NO incluye funciones de plataforma.
const NAV_ADMINISTRACION = [
  { icon: Building2, label: "Mi empresa",       path: "/panel/configuracion",     roles: ["*"], adminOnly: true, testid: "nav-mi-empresa" },
  { icon: UserCog,   label: "Usuarios y roles", path: "/panel/admin/usuarios",    roles: ["*"], adminOnly: true, mod: "usuarios_permisos", testid: "nav-usuarios-tenant" },
  { icon: UserCog,   label: "Roles internos",   path: "/panel/admin/roles",       roles: ["*"], celebraGroupOnly: true, testid: "nav-roles-internos" },
  { icon: Building2, label: "Unidades de negocio", path: "/panel/admin/unidades", roles: ["*"], adminOnly: true, mod: "unidades_multiples", testid: "nav-unidades-config",
    tooltip: "Configura tus marcas o áreas del negocio (ej. Petit, Kermesita, Bocatto). Cada unidad puede tener su propio catálogo, calendario y branding." },
  { icon: Palette,   label: "Branding",         path: "/panel/branding",          roles: ["*"], adminOnly: true, testid: "nav-branding" },
  { icon: Zap,       label: "Cotizador público",path: "/panel/cotizador-config",  roles: ["*"], adminOnly: true, mod: "cotizador_publico", testid: "nav-cotizador-publico" },
];

// SOLO SUPERADMIN DE PLATAFORMA (celebramid). Nunca aparece a tenants SaaS.
const NAV_PLATAFORMA = [
  { icon: Building2, label: "Empresas SaaS",    path: "/panel/admin/empresas",       roles: ["*"], superAdminOnly: true, testid: "nav-empresas-saas",
    tooltip: "Panel del superadmin: todas las empresas registradas en CelebraMid, sus planes y estado (activa, prueba, cancelada)." },
  { icon: Palette,   label: "Páginas públicas", path: "/panel/unidades-publicas",    roles: ["*"], superAdminOnly: true, testid: "nav-paginas-publicas",
    tooltip: "Editor de las páginas públicas de cada unidad (Petit, Kermesita, Bocatto): banners, textos, catálogo mostrado." },
  { icon: Lock,      label: "Auditoría",        path: "/panel/admin/auditoria",      roles: ["*"], superAdminOnly: true, testid: "nav-auditoria",
    tooltip: "Verifica la integridad de datos: usuarios sin empresa, contratos huérfanos, pagos con saldo negativo, eventos duplicados. Repara con un clic." },
  { icon: FileWarning, label: "Sin clasificar", path: "/panel/admin/sin-clasificar", roles: ["*"], superAdminOnly: true, testid: "nav-sin-clasificar",
    tooltip: "Eventos que llegaron sin asignar a Petit/Kermesita/Bocatto. Los clasificas aquí para que aparezcan en la unidad correcta." },
];

const SIDEBAR_AREAS_BASE = [
  { label: null, items: NAV_PRINCIPAL },
  { label: null, items: NAV_PRINCIPAL_FALLBACK_BASIC, showOnlyIfMissingMod: "eventos" },
  { label: "Administración", items: NAV_ADMINISTRACION, adminOnly: true },
  { label: "Plataforma", items: NAV_PLATAFORMA, superAdminOnly: true },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const { branding } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (sidebarOpen) document.body.classList.add("sidebar-open");
    else document.body.classList.remove("sidebar-open");
    return () => document.body.classList.remove("sidebar-open");
  }, [sidebarOpen]);

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const isSuperAdmin = user?.role === "superadmin";
  const isAdmin = isSuperAdmin || user?.role === "admin" || user?.workspace_rol === "admin";
  const esCelebraGroup = !!user?.es_celebra_group;

  // Rol interno del usuario (para filtrar navegación)
  const rolInterno = (user?.rol_interno || user?.workspace_rol || "").toLowerCase();
  // Si es admin/superadmin, todos los items visibles (bypass de rol interno)
  const bypassRoles = isAdmin;

  // Módulos del plan SaaS
  const planModulos = user?.modulos_efectivos || user?.plan_modulos;  // Iter114d · Única fuente de verdad: intersección plan ∩ rol calculada por backend
  const filterByPlan = !isSuperAdmin && Array.isArray(planModulos) && planModulos.length > 0;
  const modulosRol = user?.modulos_rol;
  const filterByRol = !isSuperAdmin && Array.isArray(modulosRol) && modulosRol.length > 0;

  const allowedMod = (mod) => {
    if (!mod) return true;
    if (filterByPlan && !planModulos.includes(mod)) return false;
    if (filterByRol && !modulosRol.includes(mod)) return false;
    // Iter149 · Preferencia visual de la empresa. Si `modulos_visibles` está
    // definida y no vacía, solo se muestran los módulos incluidos ahí.
    // "resumen" y campos sin `mod` siempre pasan (protegidos arriba).
    const mv = user?.modulos_visibles;
    if (Array.isArray(mv) && mv.length > 0 && mod !== "resumen" && !isSuperAdmin && !mv.includes(mod)) return false;
    return true;
  };

  const allowedRole = (roles) => {
    // Si no hay restricción de rol, todos pueden verlo.
    if (!roles) return true;
    // Admin/superadmin siempre bypass del filtro de rol interno.
    if (bypassRoles) return true;
    // "*" significa "cualquier rol admin/staff". Solo aplica si el usuario NO tiene rol_interno específico
    // (heredado del comportamiento previo para users legacy sin rol_interno).
    if (!rolInterno) return roles.includes("*");
    // Usuario con rol_interno específico: solo ve items que listan explícitamente su rol.
    return roles.includes(rolInterno);
  };

  const areas = SIDEBAR_AREAS_BASE
    .filter(a => !a.adminOnly || isAdmin)
    .filter(a => !a.superAdminOnly || isSuperAdmin)
    .filter(a => !a.celebraGroupOnly || esCelebraGroup)
    .filter(a => !a.showOnlyIfMissingMod || (filterByPlan && !planModulos.includes(a.showOnlyIfMissingMod)))
    .map(a => ({
      ...a,
      items: a.items
        .filter(it => {
          if (it.superAdminOnly) return isSuperAdmin;
          if (it.celebraGroupOnly && !esCelebraGroup) return false;
          if (!allowedRole(it.roles)) return false;
          return allowedMod(it.mod);
        })
        // Iter120b · Filtrar hijos por plan_modulos y ocultar grupo padre si no queda ninguno
        .map(it => {
          if (!it.children) return it;
          const kidsVisibles = it.children.filter(k => allowedMod(k.mod));
          return { ...it, children: kidsVisibles };
        })
        .filter(it => !it.children || it.children.length > 0),
    }))
    .filter(a => a.items.length > 0);

  const handleLogout = async () => { await logout(); navigate("/"); };
  const isActive = (path) => location.pathname === path || (path !== "/panel" && location.pathname.startsWith(path));

  // Grupos expandibles (Catálogo). Se expanden automáticamente si estás en cualquiera de sus hijos.
  const [openGroups, setOpenGroups] = useState({});
  const isGroupOpen = (item) => {
    if (!item.children) return false;
    if (openGroups[item.path] !== undefined) return openGroups[item.path];
    // Auto-abrir si la ruta actual coincide con alguno de los hijos
    return item.children.some(c => isActive(c.path));
  };
  const toggleGroup = (item) => {
    const currentlyOpen = isGroupOpen(item);
    setOpenGroups(prev => ({ ...prev, [item.path]: !currentlyOpen }));
  };

  // Iter124 · P0 · Roles solo-lectura-asignada: no ven "Cotizar" ni "Nuevo evento"
  const esRolSoloLectura = ["mesero", "chef", "cliente"].includes(rolInterno);

  const bottomNav = [
    { icon: Home, label: "Inicio", path: "/panel/mi-panel" },
    { icon: Calendar, label: "Agenda", path: "/panel/mi-agenda-personal" },
    ...(esRolSoloLectura ? [] : [{ icon: Zap, label: "Cotizar", path: "/panel/cotizar", primary: true }]),
    { icon: PartyPopper, label: "Eventos", path: "/panel/eventos" },
    // Iter114 · antes era un link a Config; ahora es "Más" que abre el drawer
    // con TODOS los módulos del plan. El usuario reportaba que "solo veía 5 tabs"
    // porque no sabía que había hamburger arriba.
    { icon: Menu, label: "Más", action: "open-drawer" },
  ];

  // Rol legible para el footer
  const rolLegible = (() => {
    if (isSuperAdmin) return "Propietario";
    if (isAdmin) return "Administrador";
    return { ventas: "Ventas", coordinacion: "Coordinación", chef: "Chef",
             mesero: "Mesero", cliente: "Cliente", finanzas: "Finanzas" }[rolInterno] || "Operación";
  })();

  return (
    <div className="min-h-screen flex" style={{ background: "var(--brand-surface)" }}>
      {/* ============ SIDEBAR DESKTOP ============ */}
      <aside className="hidden md:flex md:flex-col md:w-60 fixed h-full" style={{ background: "var(--brand-surface-card)", borderRight: "1px solid var(--brand-border)" }}>
        <div className="px-6 py-6">
          <Link to="/panel/mi-panel" className="block">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.brand_name} className="h-9 w-auto" />
            ) : (
              <div>
                <p className="font-display text-3xl leading-none" style={{ color: "var(--brand-accent)", fontFamily: "var(--brand-font-display)" }}>
                  {(branding.brand_name || "Celebra").split(" ")[0]}
                </p>
                <p className="text-[10px] tracking-[0.32em] mt-1" style={{ color: "var(--brand-accent)" }}>
                  {(branding.brand_name || "Celebra MID").split(" ").slice(1).join(" ") || "MID"}
                </p>
              </div>
            )}
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto pb-4" data-testid="sidebar-nav">
          {areas.map((area, idx) => (
            <div key={idx} className={idx > 0 ? "mt-5" : ""}>
              {area.label && <p className="eo-section-label">{area.label}</p>}
              {area.items.map((item) => {
                const active = isActive(item.path);
                if (item.children) {
                  const groupOpen = isGroupOpen(item);
                  return (
                    <div key={`${area.label || "root"}-${item.label}`}>
                      <button
                        type="button"
                        onClick={() => toggleGroup(item)}
                        className={`sidebar-link w-full ${active ? "active" : ""}`}
                        data-testid={`${item.testid}-toggle`}
                      >
                        <item.icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                        <span className="flex-1 text-left">{item.label}</span>
                        <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${groupOpen ? "rotate-90" : ""}`} strokeWidth={2} />
                      </button>
                      {groupOpen && (
                        <div className="ml-3 pl-3" style={{ borderLeft: "1px solid var(--brand-border)" }}>
                          {item.children.map(sub => {
                            const subActive = isActive(sub.path);
                            return (
                              <Link
                                key={sub.path}
                                to={sub.path}
                                className={`sidebar-link ${subActive ? "active" : ""}`}
                                data-testid={sub.testid}
                              >
                                <sub.icon className="w-[15px] h-[15px]" strokeWidth={1.75} />
                                <span className="text-[13px]">{sub.label}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <Link
                    key={`${area.label || "root"}-${item.label}`}
                    to={item.path}
                    className={`sidebar-link ${active ? "active" : ""}`}
                    data-testid={item.testid || `nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    title={item.tooltip || item.label}
                  >
                    <item.icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div style={{ borderTop: "1px solid var(--brand-border)" }} className="p-3">
          <div className="flex items-center gap-2">
            <NotificacionesBell />
            <DropdownMenu>
              <DropdownMenuTrigger className="flex-1 flex items-center gap-2.5 p-2 rounded-xl transition hover:bg-[var(--brand-surface-muted)]" data-testid="user-menu-trigger">
                <Avatar className="w-9 h-9">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="text-white text-sm" style={{ background: "var(--brand-primary)" }}>
                    {user?.name?.charAt(0) || "A"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: "var(--brand-text)" }}>{user?.name}</p>
                  <p className="text-[11px] truncate" style={{ color: "var(--brand-text-muted)" }}>{rolLegible}</p>
                </div>
                <ChevronDown className="w-4 h-4 shrink-0" style={{ color: "var(--brand-text-muted)" }} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate("/panel/mi-suscripcion")} data-testid="dropdown-mi-cuenta">
                  <User className="w-4 h-4 mr-2" /> Mi cuenta
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/panel/branding")} data-testid="dropdown-branding">
                  <Palette className="w-4 h-4 mr-2" /> Marca & colores
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/panel/configuracion")} data-testid="dropdown-config">
                  <Settings className="w-4 h-4 mr-2" /> Configuración
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="btn-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* ============ MOBILE TOP BAR ============ */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50" style={{ background: "var(--brand-surface-card)", borderBottom: "1px solid var(--brand-border)" }}>
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => setSidebarOpen(true)} className="flex items-center gap-2 p-2 rounded-lg hover:bg-[var(--brand-surface-muted)]" data-testid="mobile-menu-toggle" aria-label="Abrir menú">
            <Menu className="w-5 h-5" style={{ color: "var(--brand-text)" }} />
            {user?.name && (
              <span className="flex flex-col text-left leading-tight max-w-[90px] overflow-hidden">
                <span className="text-[11px] font-medium truncate" style={{ color: "var(--brand-text)" }}>{user.name.split(" ")[0]}</span>
                <span className="text-[9px] uppercase tracking-wider truncate" style={{ color: "var(--brand-text-muted)" }}>{rolLegible}</span>
              </span>
            )}
          </button>
          <Link to="/panel/mi-panel" className="text-center">
            <p className="font-display text-xl leading-none" style={{ color: "var(--brand-accent)" }}>{(branding.brand_name || "Celebra").split(" ")[0]}</p>
            <p className="text-[8px] tracking-[0.3em]" style={{ color: "var(--brand-accent)" }}>{(branding.brand_name || "MID").split(" ").slice(1).join(" ") || "MID"}</p>
          </Link>
          <div className="flex items-center gap-1">
            <NotificacionesBell />
            <Avatar className="w-8 h-8">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="text-white text-sm" style={{ background: "var(--brand-primary)" }}>
                {user?.name?.charAt(0) || "A"}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>
      </div>

      {/* ============ MOBILE SIDEBAR DRAWER ============ */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-sidebar-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-72 h-full overflow-y-auto" style={{ background: "var(--brand-surface-card)" }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid var(--brand-border)" }}>
              <p className="font-display text-xl" style={{ color: "var(--brand-accent)" }}>{branding.brand_name || "Celebra MID"}</p>
              <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-lg hover:bg-[var(--brand-surface-muted)]" data-testid="mobile-close-sidebar">
                <X className="w-5 h-5" style={{ color: "var(--brand-text)" }} />
              </button>
            </div>
            <nav className="pt-3 pb-6">
              {areas.map((area, idx) => (
                <div key={idx} className={idx > 0 ? "mt-4" : ""}>
                  {area.label && <p className="eo-section-label">{area.label}</p>}
                  {area.items.map((item) => {
                    const active = isActive(item.path);
                    if (item.children) {
                      const groupOpen = isGroupOpen(item);
                      return (
                        <div key={`m-${area.label || "root"}-${item.label}`}>
                          <button
                            type="button"
                            onClick={() => toggleGroup(item)}
                            className={`sidebar-link w-full ${active ? "active" : ""}`}
                            data-testid={`mobile-${item.testid}-toggle`}
                          >
                            <item.icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                            <span className="flex-1 text-left">{item.label}</span>
                            <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${groupOpen ? "rotate-90" : ""}`} strokeWidth={2} />
                          </button>
                          {groupOpen && (
                            <div className="ml-3 pl-3" style={{ borderLeft: "1px solid var(--brand-border)" }}>
                              {item.children.map(sub => {
                                const subActive = isActive(sub.path);
                                return (
                                  <Link
                                    key={`m-${sub.path}`}
                                    to={sub.path}
                                    className={`sidebar-link ${subActive ? "active" : ""}`}
                                    data-testid={`mobile-${sub.testid}`}
                                  >
                                    <sub.icon className="w-[15px] h-[15px]" strokeWidth={1.75} />
                                    <span className="text-[13px]">{sub.label}</span>
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return (
                      <Link
                        key={`m-${area.label || "root"}-${item.label}`}
                        to={item.path}
                        className={`sidebar-link ${active ? "active" : ""}`}
                        data-testid={`mobile-${item.testid || item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      >
                        <item.icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              ))}
              <div style={{ borderTop: "1px solid var(--brand-border)" }} className="mt-4 pt-4 mx-4">
                <button onClick={handleLogout} className="w-full flex items-center gap-2.5 p-2 rounded-lg text-red-600 hover:bg-red-50" data-testid="mobile-logout">
                  <LogOut className="w-4 h-4" /> Cerrar sesión
                </button>
              </div>
            </nav>
          </aside>
        </div>
      )}

      {/* ============ MAIN CONTENT ============ */}
      <main className="flex-1 md:ml-60 min-w-0 max-w-full min-h-screen pb-20 md:pb-8 pt-16 md:pt-0 overflow-x-hidden" data-testid="main-content">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-8 py-4 sm:py-6 md:py-10">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>

      {/* ============ MOBILE BOTTOM NAV ============ */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40" style={{ background: "var(--brand-surface-card)", borderTop: "1px solid var(--brand-border)" }}>
        <div className={esRolSoloLectura ? "grid grid-cols-4" : "grid grid-cols-5"}>
          {bottomNav.map((item) => {
            const active = item.path && isActive(item.path);
            const isDrawer = item.action === "open-drawer";
            const Wrapper = isDrawer ? "button" : Link;
            const wrapperProps = isDrawer
              ? { onClick: () => setSidebarOpen(true), type: "button" }
              : { to: item.path };
            return (
              <Wrapper key={item.label} {...wrapperProps} className="flex flex-col items-center gap-1 py-2 px-1" data-testid={`bottom-${item.label.toLowerCase()}`}>
                {item.primary ? (
                  <span className="w-11 h-11 rounded-full flex items-center justify-center -mt-6 shadow-lg" style={{ background: "var(--brand-primary)" }}>
                    <item.icon className="w-5 h-5 text-white" />
                  </span>
                ) : (
                  <item.icon className={`w-5 h-5 ${active ? "text-[var(--brand-primary)]" : ""}`} style={!active ? { color: "var(--brand-text-muted)" } : undefined} />
                )}
                <span className={`text-[10px] ${item.primary ? "font-medium" : ""}`} style={{ color: active ? "var(--brand-primary)" : "var(--brand-text-muted)" }}>
                  {item.label}
                </span>
              </Wrapper>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default AdminLayout;
